import React, { useEffect, useMemo, useState } from 'react';
import { 
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Collapse,
  IconButton,
  Image,
  Avatar,
  Spinner,
  Box,
  Flex,
  Heading,
  Divider,
 } from '@chakra-ui/react';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';

import { EquipmentModel } from '../../api/models/equipment';
import { EquipmentsGuideFormInstance } from './types';
import api from '../../api/api';
import FieldView from '../common/field-view';
import GalleryModal from '../common/modals/gallery';
import { Txt } from '../common/text';

export default function ModelCard({ item }: { item: EquipmentModel }) {
  const [expanded, setExpanded] = React.useState(false);

  const [instance, setInstance] = useState<
    EquipmentsGuideFormInstance | undefined
  >(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [image, setImage] = useState<string>();

  const [showGallery, setShowGallery] = useState<boolean>(false);

  const images = useMemo(() => item.images?.sort(({ last_update: a }, { last_update: b }) => {
    if (a && b) {
      return new Date(b).getTime() - new Date(a).getTime();
    }
    return 0;
  }) || [], [item.images]);

  useEffect(() => {
    (async () => {
      if (!images?.[0]?.image_pid) {
        return;
      }
      const { data } = await api.file.get('' + images[0].image_pid);
      setImage('data:image/jpeg;base64,' + data);
    })();
  }, [item.id, images]);

  const handleExpandClick = async () => {
    setExpanded(!expanded);
    if (!expanded) {
      setLoading(true);
      const { data: response } = await api.equipments.getEquipmentModel('' + item.id);
      setInstance(response.data);
      setLoading(false);
    }
  };

  return (<>
      <Card mb={4}>
        <CardHeader>
          <Flex gap='4'>
            <Flex flex='1' gap='4' alignItems='center' flexWrap='wrap'>
              <Avatar name={item.name} />
              <Box>
                <Heading size='sm'>{item.name}</Heading>
                <Txt>{item.manufacturer.name}</Txt>
              </Box>
            </Flex>
          </Flex>
        </CardHeader>
        <CardBody>
          <Box maxH="calc(100vh - 200px)" display='flex'>
            <Image
              src={image}
              alt={item.name}
              borderRadius='lg'
              onClick={() => setShowGallery(true)}
              objectFit='contain'
            />
          </Box>
          <Txt color="textSecondary" mt={2}>
            {item.description}
          </Txt>
        </CardBody>
        <Divider />
        <CardFooter>
          <Flex ml={1} justify='space-between' w='100%'>
            <Txt variant="caption">
              На балансе <b>{item.on_balance}</b> шт
            </Txt>
            <IconButton
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
              icon={expanded ? <MdExpandLess size={24} /> : <MdExpandMore size={24} />}
              size='sm'
            />
          </Flex>
        </CardFooter>
        <Collapse in={expanded} unmountOnExit>
          <CardBody>
            {loading && (
              <Box width="100%" display="flex" justifyContent="center">
                <Spinner />
              </Box>
            )}
            {instance && (
              <>
                {instance.height && instance.width && instance.depth && (
                  <FieldView
                    fullWidth
                    title="Габариты (ВхШхГ), мм"
                    value={`${instance.height}х${instance.width}х${instance.depth}`}
                    direction="row"
                  />
                )}
                <FieldView
                  fullWidth
                  title={
                    <>
                      Занимаемая площадь пола, м<sup>2</sup>
                    </>
                  }
                  value={instance.volume_on_floor}
                  direction="row"
                />
                <FieldView
                  fullWidth
                  title="Внутренний объем, л"
                  value={instance.volume_inner}
                  direction="row"
                />
                <FieldView
                  fullWidth
                  title="Вес, кг"
                  value={instance.weight}
                  direction="row"
                />
                <FieldView
                  fullWidth
                  title="Энергопотребление, кВт/сутки"
                  value={instance.consumption_energy}
                  direction="row"
                />
                <FieldView
                  fullWidth
                  title="Хладагент"
                  value={instance.coolant}
                  direction="row"
                />
                <FieldView
                  fullWidth
                  title="Материал внутреннего корпуса"
                  value={instance.material_inner}
                  direction="row"
                />
                <FieldView
                  fullWidth
                  title="Количество полок"
                  value={instance.shelves}
                  direction="row"
                />
                <FieldView
                  fullWidth
                  title="Возможность использования на улице"
                  value={instance.is_use_outdoor ? 'да' : 'нет'}
                  direction="row"
                />
                <FieldView
                  fullWidth
                  title="Возможность установки замка"
                  value={instance.is_use_lock ? 'да' : 'нет'}
                  direction="row"
                />
                <FieldView
                  fullWidth
                  title="Возможность установки обогрева"
                  value={instance.is_adding_heater ? 'да' : 'нет'}
                  direction="row"
                />
              </>
            )}
          </CardBody>
        </Collapse>
      </Card>
      <GalleryModal
        images={images}
        open={showGallery}
        onClose={() => setShowGallery(false)}
        title="Изображения"
        icon={<div />}
      />
    </>
  );
}
